/**
 * Local-only thread / ask-question actions (CT-5096 / CT-4867 gap).
 *
 * Close / reopen / mark-read / post-comment / ask-question have NO Platform
 * endpoint yet. These mocks mirror the forward-looking seam in
 * {@link eventDetail.js} so WorkQueueProvider can call a service layer today
 * and swap to real POSTs when CT-4867 ships.
 */

const NOT_IMPLEMENTED = 'NOT_IMPLEMENTED';

function mockDelay() {
  return Promise.resolve();
}

/**
 * Marks a Question row as read in the inbox (local-only).
 * @param {string} eventId
 * @returns {Promise<{ eventId: string, status: 'read' }>}
 */
export async function markQuestionRead(eventId) {
  if (!eventId) {
    const error = new Error('eventId is required');
    error.code = NOT_IMPLEMENTED;
    throw error;
  }
  await mockDelay();
  return { eventId, status: 'read' };
}

/**
 * Closes the per-event thread panel (local-only).
 * @param {string} eventId
 * @returns {Promise<{ eventId: string, status: 'closed' }>}
 */
export async function closeEventThread(eventId) {
  if (!eventId) {
    const error = new Error('eventId is required');
    error.code = NOT_IMPLEMENTED;
    throw error;
  }
  await mockDelay();
  return { eventId, status: 'closed' };
}

/**
 * Reopens a closed thread (local-only).
 * @param {string} eventId
 * @returns {Promise<{ eventId: string, status: 'open' }>}
 */
export async function reopenEventThread(eventId) {
  if (!eventId) {
    const error = new Error('eventId is required');
    error.code = NOT_IMPLEMENTED;
    throw error;
  }
  await mockDelay();
  return { eventId, status: 'open' };
}

/**
 * Appends a comment to the thread (local-only).
 * @param {{ eventId: string, body: string, mentions?: string[] }} params
 * @returns {Promise<{ eventId: string, commentId: string }>}
 */
export async function postThreadComment({ eventId, body, mentions = [] } = {}) {
  const text = body?.trim();
  if (!eventId || !text) {
    const error = new Error('eventId and body are required');
    error.code = NOT_IMPLEMENTED;
    throw error;
  }
  await mockDelay();
  return {
    eventId,
    commentId: `comment-${Date.now()}`,
    body: text,
    mentions,
  };
}

/**
 * Reply → Ask question: creates a follow-up question task and a QUESTION row on
 * the source event thread (local-only — prototype aims-htl).
 *
 * @param {{ sourceEventId: string, recipient: Object, question: string,
 *           context?: string, dueBy?: string, askedBy: Object }} params
 * @returns {Promise<Object>} ids + thread row payload for the provider store
 */
export async function createAskQuestion({
  sourceEventId,
  recipient,
  question,
  context = '',
  dueBy = null,
  askedBy,
} = {}) {
  const text = question?.trim();
  if (!sourceEventId || !recipient?.id || !text || !askedBy?.name) {
    const error = new Error('sourceEventId, recipient, question, and askedBy are required');
    error.code = NOT_IMPLEMENTED;
    throw error;
  }
  await mockDelay();
  const questionEventId = `EVT-Q-${Date.now()}`;
  const threadCommentId = `awaiting-${questionEventId}`;
  return {
    sourceEventId,
    questionEventId,
    threadCommentId,
    question: text,
    context: context?.trim() ?? '',
    dueBy,
    recipient,
    askedBy: {
      ...askedBy,
      initials: askedBy.initials ?? askedBy.name.slice(0, 2).toUpperCase(),
    },
    threadEntry: {
      id: threadCommentId,
      kind: 'question',
      body: text,
      questionEventId,
      questionStatus: 'awaiting_response',
      questionRecipient: recipient.name,
      author: askedBy.name,
      role: askedBy.role ?? '',
      initials: askedBy.initials ?? askedBy.name.slice(0, 2).toUpperCase(),
    },
  };
}
